import axios, { type AxiosError, type AxiosResponse } from 'axios';
import Util from '../util';
import type {
  subscriptionPackageInterface,
  CredentialInterface,
  WalletRetrieveVCPayloadInt,
  InternalOrchestrationVCInterface,
} from '../interfaces/verify/IGetVerified';
import type {
  legalPersonPayloadDataInterface,
  serviceOfferingPayloadDataInterface,
  resourcePayloadDataInterface,
  bulkVCPayloadData,
} from '../../modules/verify/pages/vcRequest/interface';
import type { FilterOptionsInterface } from '../../modules/verify/store/IGetVerified';

const getVerifiedService = {
  getAllCountries: (locale: string) => {
    return axios.get(Util.getRestApiUrl(`country?${locale}`, 'org'));
  },

  getRegions: (country: string) => {
    return axios.get(Util.getRestApiUrl(`region?isoCode=${country}`, 'org'));
  },

  fetchSubscriptionData: (orgId: string): Promise<{ data: subscriptionPackageInterface, status: number }> => {
    return axios.get(Util.getRestApiUrl(`subscriptions/fetch/${orgId}`, 'digital-id'));
  },

  requestIntegration: (requestPayload: unknown) => {
    return axios.post(Util.getRestApiUrl('technical', 'digital-id'), requestPayload, {
      headers: {
        UI: true,
      },
    });
  },

  editIntegration: (requestPayload: unknown) => {
    return axios.post(Util.getRestApiUrl('technical/callbackProperties', 'digital-id'), requestPayload, {
      headers: {
        UI: true,
      },
    });
  },

  getWalletVC: (requestPayload: WalletRetrieveVCPayloadInt): Promise<{ data: CredentialInterface, status: number }> => {
    return axios.post(Util.getRestApiUrl('credentials/internal/get', 'digital-id'), requestPayload);
  },

  issueWalletVC: (
    requestPayload: legalPersonPayloadDataInterface | serviceOfferingPayloadDataInterface | resourcePayloadDataInterface | bulkVCPayloadData,
  ): Promise<{ data: InternalOrchestrationVCInterface[], status: number }> => {
    return axios.post(Util.getRestApiUrl('credentials/internal/issue', 'digital-id'), requestPayload);
  },

  downloadVP: (uuid: string): Promise<{ data: any, status: number }> => {
    return axios.get(Util.getRestApiUrl(`compliance/json/${uuid}`, 'digital-id'));
  },

  getMIMETypes: (): Promise<{ data: string[], status: number }> => {
    return axios.get(Util.getRestApiUrl('dictionary/mime-types?filter=', 'digital-id'));
  },

  getLicensesData: (): Promise<{ data: FilterOptionsInterface[], status: number }> => {
    return axios.get(Util.getRestApiUrl('licenses', 'digital-id'));
  },

  fetchVCResolvableLinks: (vcTypes: string): Promise<{ data: FilterOptionsInterface[], status: number }> => {
    return axios.post(Util.getRestApiUrl(`credentials/internal/get/list?vcTypes=${vcTypes}`, 'digital-id'));
  },

  deleteCredential: (credentialId: string): Promise<AxiosError | AxiosResponse> => {
    return axios.delete(Util.getRestApiUrl(`credentials/internal?credentialId=${credentialId}`, 'digital-id'));
  },
};

export default getVerifiedService;
